import RestoreIcon from "../../../../atom/icons/Restore";
import { useConfirmPopup } from "../../../../../hooks/popup/useOpenConfirmPopup";
import { ConfirmPopupResult, ConfirmPopupVariant } from "../../../../../types/popup";
import { juneTrack } from "../../../../../utils/june/analytics";
import { JUNE_EVENT, JUNE_LOCATION } from "../../../../../hooks/june/juneEvent";
import { isAxiosError } from "axios";
import { useSetRecoilState } from "recoil";
import { toastState } from "../../../../../recoil/toast/toastState";
import {
  getTargetFromBlockType,
  getToastMessageByKind,
} from "../../../../../utils/toast/toastUtils";
import { ToastKind } from "../../../../../types/toast";
import { logger } from "../../../../../utils/logger";
import { ScrollArea } from "../../../../atom/ScrollArea";
import TrashIcon from "../../../../atom/icons/TrashIcon";
import { useGetBlocksQuery } from "../../../../../react-query/block/queries";
import useApi from "../../../../../services/auth/useApi";
import { BlockRequestQueryTypeParam, ItemStatus } from "../../../../../types/block/enum";
import InboxBlockCheckbox from "../../../../TaskInbox/InboxBody/InboxBlock/InboxBlockCheckbox";
import InboxBlockSummary from "../../../../TaskInbox/InboxBody/InboxBlock/InboxBlockSummary";
import InboxBlockRightSection from "../../../../TaskInbox/InboxBody/InboxBlock/InboxBlockRightSection";
import {
  usePermanentlyDeleteBlocksMutation,
  usePermanentlyDeleteBlockMutation,
  useRestoreBlockMutation,
} from "../../../../../react-query/block/mutations";
import { BlockType } from "../../../../../types/block/type";
import { debounceInvalidate } from "../../../../../services/optimistic-updates/debounceInvalidate";
import { calendarQueryKeys } from "../../../../../react-query/calendar/queries";
import { inboxQueryKeys } from "../../../../../react-query/inbox/queries";
import TrashListHoverButton from "./TrashListHoverButton";
import TrashEmptyButton from "./TrashEmptyButton";

const TaskTabContent = () => {
  const setToast = useSetRecoilState(toastState);

  const instance = useApi();
  const { data: blocksInTrash, refetch: refetchDeletedBlocks } = useGetBlocksQuery({
    instance,
    payload: {
      queryType: BlockRequestQueryTypeParam.TRASH,
    },
  });

  const openConfirm = useConfirmPopup();
  const { mutate: restoreBlock } = useRestoreBlockMutation({
    instance,
  });
  const { mutate: deleteBlock } = usePermanentlyDeleteBlockMutation({
    instance,
  });
  const { mutate: deleteAllBlock } = usePermanentlyDeleteBlocksMutation({
    instance,
  });

  /** Task 1개 삭제 */
  async function handleDeleteTask(blockId: number) {
    const confirmResult = await openConfirm({
      headerTitle: "Permanently Delete",
      message: "Are you sure you want to permanently delete the item? You can't undo this action.",
      discardButtonTitle: "Cancel",
      doNotSendButtonTitle: "",
      okButtonTitle: "Delete",
      variant: ConfirmPopupVariant.DELETE,
    });

    // 사용자가 취소한 경우
    if (confirmResult === ConfirmPopupResult.CANCEL) return;

    deleteBlock(blockId, {
      onSuccess: () => {
        refetchDeletedBlocks();

        juneTrack(JUNE_EVENT.DELETE_BLOCK, {
          type: "task",
          location: JUNE_LOCATION.TRASH,
        });
      },
      onError: (error) => {
        logger.error("Failed to delete task:", error);
      },
    });
  }

  /** Task 1개 복구 */
  function handleRestoreTask(blockData: BlockType) {
    // `lastItemStatus`가 `deleted`일 수 있는 경우가 있음, 이 경우 restore 후에도 복구되지 않는 문제가 발생
    const itemStatus =
      blockData.itemLastStatus && blockData.itemLastStatus !== ItemStatus.DELETED
        ? blockData.itemLastStatus
        : ItemStatus.IN_PROGRESS;
    // Debug 함께 할 수 있도록 logger 추가
    if (blockData.itemLastStatus === ItemStatus.DELETED) {
      logger.debug(`Restoring block with itemLastStatus is deleted, blockId: ${blockData.id}`);
    }
    // 이전 state가 없으면 `inProgress`로 복구
    restoreBlock(
      {
        id: blockData.id,
        data: { itemStatus },
      },
      {
        onSuccess: (responseData) => {
          setToast({
            isVisible: true,
            message: getToastMessageByKind({
              type: ToastKind.RESTORE,
              target: getTargetFromBlockType(responseData.blockType),
            }),
            type: ToastKind.RESTORE,
            triggerId: Date.now(),
          });

          refetchDeletedBlocks();

          // 복구된 data fetching
          // TODO: 좀 더 세분화하여 invalidate
          if (responseData.start) {
            debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
              shouldInvalidateImmediately: true,
            });
          }
          debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
            shouldInvalidateImmediately: true,
          });

          juneTrack(JUNE_EVENT.RESTORE_BLOCK);
        },
        onError: (error) => {
          if (isAxiosError(error)) {
            debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
              shouldInvalidateImmediately: true,
            });
            debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
              shouldInvalidateImmediately: true,
            });

            setToast({
              isVisible: true,
              type: ToastKind.ERROR,
              message: getToastMessageByKind({
                type: ToastKind.ERROR,
                action: "restore",
              }),
              triggerId: Date.now(),
            });
          }
        },
      }
    );
  }

  /** Tasks tab 전체 삭제 */
  function handleDeleteAllBlocks() {
    if (!blocksInTrash) return;

    const blockIds = blocksInTrash.map((block: BlockType) => block.id);

    deleteAllBlock(blockIds, {
      onSuccess: () => {
        refetchDeletedBlocks();
        juneTrack(JUNE_EVENT.CLICK_EMPTY, {
          type: "task",
        });
      },
    });
  }

  async function handleTaskEmpty() {
    const confirmResult = await openConfirm({
      headerTitle: "Empty Trash",
      message: "Are you sure you want to permanently delete the items? You can't undo this action.",
      discardButtonTitle: "Cancel",
      doNotSendButtonTitle: "", // 숨김
      okButtonTitle: "Delete",
      variant: ConfirmPopupVariant.EMPTY,
    });

    // NOTE 사용자가 취소한 경우
    if (confirmResult === ConfirmPopupResult.CANCEL) return;

    handleDeleteAllBlocks();
  }

  if (!blocksInTrash || blocksInTrash.length === 0) return null;

  return (
    <div className="flex h-full flex-col">
      <ScrollArea
        height={430}
        showScrollBar={true}
        className="grow"
      >
        <ul className="flex flex-col gap-[8px] py-[4px] pl-[8px]">
          {blocksInTrash.map((block) => (
            <li
              key={block.id}
              className={
                "group relative flex h-9 w-full select-none items-center gap-1.5 rounded-[5px] pl-2 text-[14px] leading-[150%] text-white hover:bg-w-8-hover data-[state=open]:bg-w-8-hover"
              }
            >
              <div className="flex min-w-0 flex-1 items-start gap-[6px] rounded-[5px] py-[6px] pl-[2px] pr-[6px]">
                <InboxBlockCheckbox
                  isCompleted={block.itemLastStatus === ItemStatus.COMPLETED}
                  projectId={block.spaceId}
                  onClick={() => {}}
                  disabled={true}
                />
                <InboxBlockSummary
                  title={block.title ?? "No title"}
                  isCompleted={block.itemLastStatus === ItemStatus.COMPLETED}
                />
                <InboxBlockRightSection
                  data={block}
                  isOverdue={false}
                  isTodayGeneral={false}
                />
              </div>
              <div className="absolute right-[4px] top-1/2 hidden -translate-y-1/2 gap-[4px] group-hover:flex">
                <TrashListHoverButton onClick={() => handleRestoreTask(block)}>
                  <RestoreIcon color="#CACDCE" />
                </TrashListHoverButton>
                <TrashListHoverButton onClick={() => handleDeleteTask(block.id)}>
                  <TrashIcon color="#CACDCE" />
                </TrashListHoverButton>
              </div>
            </li>
          ))}
        </ul>
      </ScrollArea>
      <div className="border-box flex h-[40px] flex-shrink-0 items-center justify-end border-t border-solid border-25 px-[12px]">
        <TrashEmptyButton onClick={handleTaskEmpty}>Empty</TrashEmptyButton>
      </div>
    </div>
  );
};

export default TaskTabContent;
